import type { BeautyShotGenerationStep, BeautyShotSlot, PersonalizationZone } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { isTerminalBeautyShotStep, useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import ShowBeautyShotGenerationStatusBatchController from '@/types/wayfinder/actions/App/Http/Controllers/Web/BeautyShots/ShowBeautyShotGenerationStatusBatchController';
import { useHttp } from '@inertiajs/vue3';
import { useIntervalFn } from '@vueuse/core';
import { computed, onUnmounted, watch } from 'vue';

const pollIntervalMs = 3000;
// Must stay above the beauty-shots:mark-stale-as-failed 10-minute sweep, or a slot fails while the job is still retrying.
const slotTimeoutMs = 11 * 60 * 1000;

type BeautyShotBatchPayload = {
    step: BeautyShotGenerationStep;
    stepLabel: string;
    resultUrl?: string;
    message?: string;
    personalizationZone?: PersonalizationZone;
    dispatchedAt?: string;
};

// Polling fallback for missed BeautyShotGenerationStatusUpdated WS events.
export function useBeautyShotPolling(): void {
    const session = useAiImageGenerationSessionStore();
    type BeautyShotStatusRequest = { correlationIds: string[] | undefined; tokens: string[] | undefined };
    const http = useHttp<BeautyShotStatusRequest, Record<string, BeautyShotBatchPayload>>({ correlationIds: undefined, tokens: undefined });

    const firstSeenAt = new Map<string, number>();

    const unsettledSlots = computed(() => session.beautyShots.filter(slot => !isTerminalBeautyShotStep(slot.step)));

    // Start as soon as slots exist: waiting for session.step 'completed' would delay catching dropped WS events.
    const shouldPoll = computed(() => unsettledSlots.value.length > 0);

    const { pause, resume } = useIntervalFn(
        async () => {
            const slots = unsettledSlots.value;

            if (slots.length === 0) {
                pause();
                return;
            }

            const now = Date.now();

            // Drop timers for settled slots, or a retried slot's original timestamp re-fails it instantly.
            session.beautyShots.forEach((slot: BeautyShotSlot) => {
                if (isTerminalBeautyShotStep(slot.step)) {
                    firstSeenAt.delete(slot.correlationId);
                }
            });

            // Force-fail slots past the timeout — the backend job is likely stuck.
            slots.forEach((slot: BeautyShotSlot) => {
                if (!firstSeenAt.has(slot.correlationId)) {
                    firstSeenAt.set(slot.correlationId, now);
                }

                const age = now - (firstSeenAt.get(slot.correlationId) ?? now);

                if (age >= slotTimeoutMs) {
                    firstSeenAt.delete(slot.correlationId);
                    session.applyBeautyShotStatusUpdate({
                        correlationId: slot.correlationId,
                        step: 'failed',
                        stepLabel: 'Timeout',
                    });
                }
            });

            const stillUnsettled = unsettledSlots.value;

            if (stillUnsettled.length === 0) {
                pause();
                return;
            }

            http.correlationIds = stillUnsettled.map(slot => slot.correlationId);
            http.tokens = stillUnsettled.map(slot => slot.token);

            await http.post(ShowBeautyShotGenerationStatusBatchController().url, {
                onSuccess: (data: Record<string, BeautyShotBatchPayload>) => {
                    Object.entries(data).forEach(([correlationId, payload]) => {
                        session.applyBeautyShotStatusUpdate({ correlationId, ...payload });

                        if (isTerminalBeautyShotStep(payload.step)) {
                            firstSeenAt.delete(correlationId);
                        }
                    });
                },
            });
        },
        pollIntervalMs,
        { immediate: true },
    );

    watch(
        shouldPoll,
        should => {
            if (should) {
                resume();
                return;
            }

            pause();
        },
        { immediate: true },
    );

    onUnmounted(() => {
        firstSeenAt.clear();
        pause();
    });
}
